import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // App name
      appName: "MijuSotong",
      appNameKorean: "미주소통",
      appTagline: "Investment Community",

      // Navigation
      nav: {
        home: "Home",
        network: "Network",
        notifications: "Notifications",
        profile: "Profile",
        watchlist: "Watchlist",
        news: "News",
        themes: "Themes",
        education: "Education",
        companies: "Companies",
        friends: "Friends"
      },

      // Auth
      auth: {
        login: "Login",
        signup: "Sign Up",
        logout: "Logout",
        email: "Email",
        password: "Password",
        confirmPassword: "Confirm Password",
        name: "Name",
        signInWithGoogle: "Continue with Google",
        dontHaveAccount: "Don't have an account?",
        alreadyHaveAccount: "Already have an account?",
        showPassword: "Show password",
        hidePassword: "Hide password",
        emailVerificationSent: "Verification email sent! Please check your inbox and verify your email before logging in.",
        emailNotVerified: "Please verify your email address before signing in. Check your inbox for the verification link.",
        resendVerification: "Resend verification email?",
        verificationEmailResent: "Verification email has been resent. Please check your inbox.",
        signupSuccess: "Sign up successful!\n\nA verification email has been sent to {email}.\n\nPlease verify your email and then log in.",
        emailAlreadyInUse: "This email is already registered. Please log in or use a different email address.",
        invalidEmail: "Please enter a valid email address.",
        weakPassword: "Password should be at least 6 characters long.",
        wrongPassword: "Incorrect password. Please try again.",
        userNotFound: "No account found with this email address. Please sign up first.",
        tooManyRequests: "Too many login attempts. Please try again later.",
        networkError: "Network error. Please check your internet connection.",
        loginSuccess: "Login Successful!",
        loginSuccessMessage: "Welcome back! You have successfully logged in.",
        signupSuccessTitle: "Sign Up Successful!",
        signupSuccessMessage: "A verification email has been sent to {email}. Please verify your email and then log in.",
        passwordMismatch: "Passwords do not match!",
        passwordMismatchMessage: "Please make sure your passwords match.",
        errorTitle: "Error",
        successTitle: "Success",
        okButton: "OK",
        resendButton: "Resend Email",
        cancelButton: "Cancel"
      },

      // Profile
      profile: {
        followers: "Followers",
        following: "Following",
        myPortfolio: "My Portfolio",
        myPredictions: "My Predictions",
        myGroups: "My Groups",
        trader: "Trader",
        dayTrader: "Day Trader"
      },

      // Post
      post: {
        whatsOnYourMind: "What's on your mind?",
        createPost: "Create Post",
        shareUpdate: "Share an update",
        prediction: "Prediction",
        bullish: "Bullish",
        bearish: "Bearish",
        neutral: "Neutral",
        tickerSymbol: "Ticker Symbol",
        addImages: "Add Images",
        feeling: "Feeling",
        activity: "Activity",
        post: "Post",
        cancel: "Cancel",
        like: "Like",
        comment: "Comment",
        share: "Share",
        comments: "Comments",
        writeComment: "Write a comment...",
        viewAllComments: "View all comments",
        hideComments: "Hide comments"
      },

      // Feelings
      feeling: {
        happy: "Happy",
        loved: "Loved",
        laughing: "Laughing",
        excited: "Excited",
        cool: "Cool",
        grateful: "Grateful",
        sad: "Sad",
        angry: "Angry",
        tired: "Tired"
      },

      // Activities
      activity: {
        trading: "Trading",
        researching: "Researching",
        learning: "Learning",
        celebrating: "Celebrating",
        analyzing: "Analyzing",
        watching: "Watching Market",
        investing: "Investing",
        thinking: "Thinking",
        planning: "Planning"
      },

      // Stock
      stock: {
        price: "Price",
        change: "Change",
        high: "High",
        low: "Low",
        open: "Open",
        volume: "Volume",
        marketCap: "Market Cap",
        chart: "Chart",
        news: "News",
        about: "About",
        addToWatchlist: "Add to Watchlist",
        removeFromWatchlist: "Remove from Watchlist",
        buyRecommendation: "Buy",
        sellRecommendation: "Sell",
        holdRecommendation: "Hold"
      },

      // Time periods
      period: {
        "1d": "1 Day",
        "5d": "5 Days",
        "1m": "1 Month",
        "3m": "3 Months",
        "6m": "6 Months",
        "1y": "1 Year",
        "5y": "5 Years"
      },

      // Sidebar
      sidebar: {
        myThemes: "My Themes",
        myWatchlist: "My Watchlist",
        viewAll: "View All",
        individualStockNews: "Individual Stock News",
        followRecommendations: "Follow Recommendations",
        addWatchlistStocks: "Add stocks to your watchlist"
      },

      // Watchlist
      watchlist: {
        myWatchlist: "My Watchlist",
        publicCompanies: "Public Companies",
        gridView: "Grid View",
        tableView: "Table View",
        loginRequired: "Login Required",
        loginRequiredMessage: "Please log in to add stocks to your watchlist.",
        guestRestriction: "Guest Restriction",
        guestRestrictionMessage: "Guest users cannot add stocks to watchlist.\nPlease log in to use this feature.",
        updateFailed: "Update Failed",
        updateFailedMessage: "Failed to update watchlist. Please try again."
      },

      // Themes
      themes: {
        myThemes: "My Themes",
        allThemes: "All Themes",
        gridView: "Grid View",
        tableView: "Table View",
        createTheme: "Create Theme",
        browseAllThemes: "Browse All Themes",
        loginRequired: "Login Required",
        loginRequiredMessage: "Please log in to add themes to your watchlist.",
        guestRestriction: "Guest Restriction",
        guestRestrictionMessage: "Guest users cannot add themes.\nPlease log in to use this feature.",
        updateFailed: "Update Failed",
        updateFailedMessage: "Failed to update theme. Please try again."
      },

      // News
      news: {
        marketNews: "Market News",
        myWatchlistNews: "My Watchlist News",
        myThemesNews: "My Themes News",
        allNews: "All News",
        allThemesNews: "All Themes News"
      },

      // Friends
      friends: {
        friendsAndConnections: "Friends & Connections",
        connectWithTraders: "Connect with other traders and share insights",
        friends: "Friends",
        received: "Received",
        sent: "Sent",
        followers: "Followers",
        following: "Following",
        yourFriends: "Your Friends",
        friendRequests: "Friend Requests",
        sentFriendRequests: "Sent Friend Requests",
        yourFollowers: "Your Followers",
        message: "Message",
        accept: "Accept",
        reject: "Reject",
        cancelRequest: "Cancel Request",
        unfriend: "Unfriend",
        remove: "Remove",
        followBack: "Follow Back",
        unfollow: "Unfollow",
        mutualFriends: "mutual friends",
        followingBack: "Following back",
        followsYou: "Follows you",
        requestSent: "Request sent",
        noFriendsFound: "No friends found.",
        noPendingRequests: "No pending friend requests.",
        noPendingSentRequests: "No pending sent requests.",
        noFollowersYet: "No followers yet.",
        notFollowingYet: "Not following anyone yet.",
        searchPlaceholder: "Search"
      },

      // Common
      common: {
        search: "Search",
        loading: "Loading...",
        error: "Error",
        success: "Success",
        viewMore: "View More",
        viewLess: "View Less",
        close: "Close",
        save: "Save",
        delete: "Delete",
        edit: "Edit",
        update: "Update",
        add: "Add",
        remove: "Remove"
      }
    }
  },
  ko: {
    translation: {
      // App name
      appName: "미주소통",
      appNameKorean: "미주소통",
      appTagline: "투자 커뮤니티",

      // Navigation
      nav: {
        home: "홈",
        network: "네트워크",
        notifications: "알림",
        profile: "프로필",
        watchlist: "관심종목",
        news: "뉴스",
        themes: "테마종목",
        education: "학습",
        companies: "기업",
        friends: "친구"
      },

      // Auth
      auth: {
        login: "로그인",
        signup: "회원가입",
        logout: "로그아웃",
        email: "이메일",
        password: "비밀번호",
        confirmPassword: "비밀번호 확인",
        name: "이름",
        signInWithGoogle: "Google로 계속하기",
        dontHaveAccount: "계정이 없으신가요?",
        alreadyHaveAccount: "이미 계정이 있으신가요?",
        showPassword: "비밀번호 표시",
        hidePassword: "비밀번호 숨기기",
        emailVerificationSent: "인증 이메일이 전송되었습니다! 로그인하기 전에 받은 편지함을 확인하고 이메일을 인증해주세요.",
        emailNotVerified: "로그인하기 전에 이메일 주소를 인증해주세요. 받은 편지함에서 인증 링크를 확인하세요.",
        resendVerification: "인증 이메일을 다시 보내시겠습니까?",
        verificationEmailResent: "인증 이메일이 다시 전송되었습니다. 받은 편지함을 확인해주세요.",
        signupSuccess: "회원가입 성공!\n\n인증 이메일이 {email}(으)로 전송되었습니다.\n\n이메일을 인증한 후 로그인해주세요.",
        emailAlreadyInUse: "이미 등록된 이메일입니다. 로그인하거나 다른 이메일 주소를 사용해주세요.",
        invalidEmail: "올바른 이메일 주소를 입력해주세요.",
        weakPassword: "비밀번호는 최소 6자 이상이어야 합니다.",
        wrongPassword: "비밀번호가 일치하지 않습니다. 다시 시도해주세요.",
        userNotFound: "해당 이메일로 등록된 계정이 없습니다. 먼저 회원가입해주세요.",
        tooManyRequests: "로그인 시도 횟수가 너무 많습니다. 나중에 다시 시도해주세요.",
        networkError: "네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.",
        loginSuccess: "로그인 성공!",
        loginSuccessMessage: "환영합니다! 성공적으로 로그인되었습니다.",
        signupSuccessTitle: "회원가입 성공!",
        signupSuccessMessage: "인증 이메일이 {email}(으)로 전송되었습니다. 이메일을 인증한 후 로그인해주세요.",
        passwordMismatch: "비밀번호 불일치!",
        passwordMismatchMessage: "비밀번호가 일치하지 않습니다. 다시 확인해주세요.",
        errorTitle: "오류",
        successTitle: "성공",
        okButton: "확인",
        resendButton: "이메일 재전송",
        cancelButton: "취소"
      },

      // Profile
      profile: {
        followers: "팔로워",
        following: "팔로잉",
        myPortfolio: "내 포트폴리오",
        myPredictions: "내 예측",
        myGroups: "내 그룹",
        trader: "트레이더",
        dayTrader: "데이 트레이더"
      },

      // Post
      post: {
        whatsOnYourMind: "무슨 생각을 하고 계신가요?",
        createPost: "게시물 작성",
        shareUpdate: "업데이트 공유",
        prediction: "예측",
        bullish: "상승",
        bearish: "하락",
        neutral: "중립",
        tickerSymbol: "티커 심볼",
        addImages: "이미지 추가",
        feeling: "기분",
        activity: "활동",
        post: "게시",
        cancel: "취소",
        like: "좋아요",
        comment: "댓글",
        share: "공유",
        comments: "댓글",
        writeComment: "댓글을 입력하세요...",
        viewAllComments: "모든 댓글 보기",
        hideComments: "댓글 숨기기"
      },

      // Feelings
      feeling: {
        happy: "행복해요",
        loved: "사랑해요",
        laughing: "웃겨요",
        excited: "설레요",
        cool: "멋져요",
        grateful: "감사해요",
        sad: "슬퍼요",
        angry: "화나요",
        tired: "피곤해요"
      },

      // Activities
      activity: {
        trading: "거래중",
        researching: "조사중",
        learning: "학습중",
        celebrating: "축하중",
        analyzing: "분석중",
        watching: "시장관찰중",
        investing: "투자중",
        thinking: "고민중",
        planning: "계획중"
      },

      // Stock
      stock: {
        price: "가격",
        change: "변동",
        high: "고가",
        low: "저가",
        open: "시가",
        volume: "거래량",
        marketCap: "시가총액",
        chart: "차트",
        news: "뉴스",
        about: "정보",
        addToWatchlist: "관심종목에 추가",
        removeFromWatchlist: "관심종목에서 제거",
        buyRecommendation: "매수",
        sellRecommendation: "매도",
        holdRecommendation: "보유"
      },

      // Time periods
      period: {
        "1d": "1일",
        "5d": "5일",
        "1m": "1개월",
        "3m": "3개월",
        "6m": "6개월",
        "1y": "1년",
        "5y": "5년"
      },

      // Sidebar
      sidebar: {
        myThemes: "테마종목",
        myWatchlist: "관심종목",
        viewAll: "전체보기",
        individualStockNews: "개별 종목 뉴스",
        followRecommendations: "팔로우 추천",
        addWatchlistStocks: "관심 종목을 추가하세요"
      },

      // Watchlist
      watchlist: {
        myWatchlist: "관심종목",
        publicCompanies: "상장기업",
        gridView: "그리드 보기",
        tableView: "테이블 보기",
        loginRequired: "로그인 필요",
        loginRequiredMessage: "관심 종목을 추가하려면 로그인이 필요합니다.",
        guestRestriction: "게스트 제한",
        guestRestrictionMessage: "게스트는 관심 종목을 추가할 수 없습니다.\n로그인 후 이용해주세요.",
        updateFailed: "업데이트 실패",
        updateFailedMessage: "관심 종목 업데이트에 실패했습니다. 다시 시도해주세요."
      },

      // Themes
      themes: {
        myThemes: "테마종목",
        allThemes: "전체 테마",
        gridView: "그리드 보기",
        tableView: "테이블 보기",
        createTheme: "테마 생성",
        browseAllThemes: "전체 테마 보기",
        loginRequired: "로그인 필요",
        loginRequiredMessage: "테마를 추가하려면 로그인이 필요합니다.",
        guestRestriction: "게스트 제한",
        guestRestrictionMessage: "게스트는 테마를 추가할 수 없습니다.\n로그인 후 이용해주세요.",
        updateFailed: "업데이트 실패",
        updateFailedMessage: "테마 업데이트에 실패했습니다. 다시 시도해주세요."
      },

      // News
      news: {
        marketNews: "시장 뉴스",
        myWatchlistNews: "관심종목 뉴스",
        myThemesNews: "테마종목 뉴스",
        allNews: "전체 뉴스",
        allThemesNews: "전체 테마 뉴스"
      },

      // Friends
      friends: {
        friendsAndConnections: "친구 및 연결",
        connectWithTraders: "다른 트레이더와 연결하고 인사이트를 공유하세요",
        friends: "친구",
        received: "받은 요청",
        sent: "보낸 요청",
        followers: "팔로워",
        following: "팔로잉",
        yourFriends: "친구 목록",
        friendRequests: "친구 요청",
        sentFriendRequests: "보낸 친구 요청",
        yourFollowers: "팔로워 목록",
        message: "메시지",
        accept: "승인",
        reject: "거절",
        cancelRequest: "요청 취소",
        unfriend: "친구 삭제",
        remove: "제거",
        followBack: "맞팔로우",
        unfollow: "언팔로우",
        mutualFriends: "공통 친구",
        followingBack: "맞팔로우 중",
        followsYou: "나를 팔로우함",
        requestSent: "요청 전송됨",
        noFriendsFound: "친구를 찾을 수 없습니다.",
        noPendingRequests: "대기 중인 친구 요청이 없습니다.",
        noPendingSentRequests: "대기 중인 보낸 요청이 없습니다.",
        noFollowersYet: "아직 팔로워가 없습니다.",
        notFollowingYet: "아직 팔로우하지 않았습니다.",
        searchPlaceholder: "검색"
      },

      // Common
      common: {
        search: "검색",
        loading: "로딩중...",
        error: "오류",
        success: "성공",
        viewMore: "더보기",
        viewLess: "접기",
        close: "닫기",
        save: "저장",
        delete: "삭제",
        edit: "편집",
        update: "업데이트",
        add: "추가",
        remove: "제거"
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'ko',
    lng: 'ko', // Default to Korean
    debug: false,

    interpolation: {
      escapeValue: false // React already handles XSS protection
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
